import {
  AlertTriangle,
  ArrowRight,
  CalendarClock,
  Check,
  ExternalLink,
  FileStack,
  LogOut,
  Mail,
  Plus,
  Radio,
  ShieldCheck,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { providerLabels } from '../data';
import type { RemotePublication, RemoteWorkspace } from '../types';
import { PlatformBadge } from './PlatformBadge';
import { StatusDot } from './StatusDot';

const cancelableStates = new Set(['SCHEDULE_REQUESTED', 'SCHEDULED', 'QUEUED', 'RETRY_WAIT']);

export function providerPermalink(publication: RemotePublication): string | undefined {
  if (
    publication.provider !== 'youtube' ||
    publication.state !== 'PUBLISHED' ||
    !/^[A-Za-z0-9_-]{11}$/.test(publication.provider_external_id ?? '')
  ) return undefined;
  return `https://www.youtube.com/watch?v=${encodeURIComponent(publication.provider_external_id!)}`;
}

export function publicationStatusLabel(state: string): string {
  return ({
    SCHEDULE_REQUESTED: 'Schedule requested',
    SCHEDULED: 'Scheduled',
    QUEUED: 'Queued',
    PROCESSING: 'Processing at provider',
    RETRY_WAIT: 'Retry pending',
    PUBLISHED: 'Published',
    FAILED: 'Failed',
    UNKNOWN: 'Needs review',
    CANCELED: 'Canceled',
    CANCELLED: 'Canceled',
  } as Record<string, string>)[state] ?? humanize(state || 'Unknown');
}

export function publicationStatusTone(state: string): 'green' | 'blue' | 'amber' | 'gray' {
  if (state === 'PUBLISHED') return 'green';
  if (['SCHEDULE_REQUESTED', 'SCHEDULED', 'QUEUED', 'PROCESSING'].includes(state)) return 'blue';
  if (['FAILED', 'UNKNOWN', 'RETRY_WAIT'].includes(state)) return 'amber';
  return 'gray';
}

export function ProductionContentView({
  remote,
  onPublish,
  onCancel,
}: {
  remote?: RemoteWorkspace;
  onPublish: () => void;
  onCancel: (publication: RemotePublication) => Promise<void>;
}) {
  return (
    <div className="view-scroll">
      <div className="view-heading">
        <div><span className="section-index">CONTENT LIBRARY</span><h1>Recorded posts and delivery history.</h1><p>Only content returned by this workspace’s backend is shown here.</p></div>
        <button className="button button--blue" onClick={onPublish}><Plus size={16} /> New recorded post</button>
      </div>
      {!remote ? <LoadingState /> : (
        <>
          <section className="real-section">
            <header><div><span className="section-index">DRAFTS</span><h2>Workspace drafts</h2></div><span>{remote.posts.length}</span></header>
            {remote.posts.length === 0
              ? <HonestEmpty icon={<FileStack size={22} />} title="No drafts yet" copy="Create a recorded post when you are ready. No sample content is inserted into live workspaces." />
              : <div className="real-post-grid">{remote.posts.map((post) => (
                <article className="real-post-card" key={post.post_id}>
                  <StatusDot tone="gray">{humanize(post.status ?? 'Draft')}</StatusDot>
                  <h3>{post.title?.trim() || 'Untitled draft'}</h3>
                  <p>{post.excerpt?.trim() || 'No caption preview is available.'}</p>
                  <dl><div><dt>Version</dt><dd>{post.current_version ?? '—'}</dd></div><div><dt>Updated</dt><dd>{formatDateTime(post.updated_at)}</dd></div></dl>
                </article>
              ))}</div>}
          </section>
          <section className="real-section">
            <header><div><span className="section-index">DELIVERY</span><h2>Publication state</h2></div><span>{remote.publications.length}</span></header>
            <PublicationList remote={remote} publications={remote.publications} onCancel={onCancel} />
          </section>
        </>
      )}
    </div>
  );
}

export function ProductionCalendarView({
  remote,
  onPublish,
  onCancel,
}: {
  remote?: RemoteWorkspace;
  onPublish: () => void;
  onCancel: (publication: RemotePublication) => Promise<void>;
}) {
  const publications = [...(remote?.publications ?? [])].sort((a, b) => (
    Date.parse(a.scheduled_at ?? '') || 0
  ) - (Date.parse(b.scheduled_at ?? '') || 0));
  return (
    <div className="view-scroll">
      <div className="view-heading">
        <div><span className="section-index">PUBLISHING SCHEDULE</span><h1>Exact scheduled publications.</h1><p>This launch view lists real backend records; drag-and-drop calendar editing is not enabled.</p></div>
        <button className="button button--blue" onClick={onPublish}><Plus size={16} /> Schedule recorded post</button>
      </div>
      {!remote ? <LoadingState /> : <PublicationList remote={remote} publications={publications} onCancel={onCancel} />}
    </div>
  );
}

export function ProductionAnalyticsView({ remote }: { remote?: RemoteWorkspace }) {
  const counts = useMemo(() => {
    const next = { scheduled: 0, processing: 0, published: 0, attention: 0 };
    for (const publication of remote?.publications ?? []) {
      if (publication.state === 'PUBLISHED') next.published += 1;
      else if (publication.state === 'PROCESSING') next.processing += 1;
      else if (['FAILED', 'UNKNOWN', 'RETRY_WAIT'].includes(publication.state)) next.attention += 1;
      else if (!['CANCELED', 'CANCELLED'].includes(publication.state)) next.scheduled += 1;
    }
    return next;
  }, [remote]);
  return (
    <div className="view-scroll">
      <div className="view-heading"><div><span className="section-index">DELIVERY OPERATIONS</span><h1>Real publishing state, not audience estimates.</h1><p>Audience reach, engagement, and provider analytics are not enabled in the launch product.</p></div></div>
      {!remote ? <LoadingState /> : <>
        <div className="metrics-grid real-metrics">
          <Metric label="Scheduled or queued" value={counts.scheduled} />
          <Metric label="Processing" value={counts.processing} />
          <Metric label="Published" value={counts.published} />
          <Metric label="Needs attention" value={counts.attention} />
        </div>
        <HonestEmpty icon={<AlertTriangle size={22} />} title="Audience analytics are not enabled" copy="This page currently reports only publication workflow states stored by ThisBot. It does not invent reach, views, engagement, or growth." />
      </>}
    </div>
  );
}

export function LiveRoadmapView({ demo }: { demo: boolean }) {
  return (
    <div className="view-scroll">
      <div className="view-heading"><div><span className="section-index">LIVE FEATURE BOUNDARY</span><h1>Live broadcast control is not enabled.</h1><p>ThisBot’s launch scope is approval-first scheduling of recorded posts. It does not create broadcasts, issue encoder ingest details, or monitor stream health.</p></div></div>
      <div className="live-status-grid">
        <article className="live-provider-card is-supported">
          <header><div><PlatformBadge provider="tiktok" /><PlatformBadge provider="instagram" /><PlatformBadge provider="facebook" /><PlatformBadge provider="youtube" /></div><StatusDot tone="blue">Launch scope</StatusDot></header>
          <h2>Recorded scheduled posts</h2>
          <p>Prepare recorded media, review each destination and setting, then explicitly approve the schedule.</p>
          <ul><li><Check size={14} /> Exact destination and content snapshot</li><li><Check size={14} /> Provider delivery state and external ID when returned</li><li><Check size={14} /> No live-broadcast claim</li></ul>
        </article>
        <article className="live-provider-card">
          <header><div><Radio size={30} /></div><StatusDot tone="gray">Not enabled</StatusDot></header>
          <h2>Live broadcasting</h2>
          <p>Live event creation, encoder ingest, readiness monitoring, and automated live start are outside the current product.</p>
          <ul><li><Check size={14} /> Start and manage live broadcasts in the provider’s own tools</li><li><Check size={14} /> Do not send stream keys or provider secrets to chat</li><li><Check size={14} /> Future availability will require provider approval and dedicated implementation</li></ul>
        </article>
      </div>
      {demo && <section className="run-of-show"><header><div><span className="section-index">DEMO CONCEPT · NO LIVE ACTIONS</span><h2>Recorded campaign checklist</h2></div><StatusDot tone="gray">Example only</StatusDot></header>{[
        'Prepare a recorded announcement post',
        'Review captions and destination settings',
        'Schedule a recorded reminder post',
        'Manage the actual live event in the provider app',
        'Return with recorded recap media',
      ].map((item, index) => <div className="run-step" key={item}><span>{String(index + 1).padStart(2, '0')}</span><p>{item}</p><Check size={16} /></div>)}</section>}
    </div>
  );
}

export function ProductionSettingsView({ remote, onSignOut }: { remote?: RemoteWorkspace; onSignOut: () => void }) {
  const brand = remote?.brands[0];
  const deletionHref = `mailto:privacy@thisbotdoesmysocialmedia.com?subject=${encodeURIComponent('Workspace deletion request')}&body=${encodeURIComponent(`Workspace: ${remote?.workspace.name ?? brand?.name ?? 'unknown'}\n\nPlease tell us whether you want a user account or full workspace deleted. Do not include passwords, access tokens, or API keys.`)}`;
  return (
    <div className="view-scroll">
      <div className="view-heading"><div><span className="section-index">WORKSPACE SETTINGS</span><h1>{brand?.name ?? remote?.workspace.name ?? 'Workspace'}</h1><p>Only settings returned by the authenticated workspace are displayed.</p></div><button className="button button--outline" onClick={onSignOut}><LogOut size={14} /> Sign out</button></div>
      {!remote ? <LoadingState /> : <div className="settings-layout production-settings">
        <aside><button className="is-active">Workspace record</button><a href="/data-deletion">Data deletion</a></aside>
        <section className="settings-form">
          <div className="settings-section-head"><div><h2>Workspace record</h2><p>Profile editing and team management are not enabled in the launch interface.</p></div><span className="tenant-avatar">{initials(brand?.name ?? remote.workspace.name ?? 'Workspace')}</span></div>
          <div className="form-grid"><label className="field-label">Brand name<input value={brand?.name ?? ''} readOnly /></label><label className="field-label">Time zone<input value={brand?.timezone ?? 'Not set'} readOnly /></label><label className="field-label">Plan<input value={humanize(remote.workspace.plan_key ?? 'Not set')} readOnly /></label><label className="field-label">Entitlement<input value={humanize(remote.workspace.entitlement_status ?? 'Not set')} readOnly /></label></div>
          <div className="settings-note"><ShieldCheck size={18} /><span><strong>Read-only launch settings</strong><p>No Save button is shown because a workspace-profile update endpoint is not implemented.</p></span></div>
          <div className="support-actions">
            <div><Mail size={18} /><span><strong>Support</strong><p>This link opens a draft in your email app. It does not submit an in-app ticket.</p></span><a className="button button--outline" href="mailto:support@thisbotdoesmysocialmedia.com?subject=ThisBot%20support%20request">Email support</a></div>
            <div><AlertTriangle size={18} /><span><strong>Account or workspace deletion</strong><p>No in-app deletion backend is enabled. Emailing starts a verified manual request.</p></span><a className="button button--danger" href={deletionHref}>Request deletion by email</a></div>
          </div>
        </section>
      </div>}
    </div>
  );
}

export function ProductionPulse({ remote, onOpenCalendar }: { remote?: RemoteWorkspace; onOpenCalendar: () => void }) {
  if (!remote) return <aside className="plan-panel production-pulse"><LoadingState /></aside>;
  const recent = [...remote.publications].sort((a, b) => Date.parse(b.updated_at ?? b.created_at ?? '') - Date.parse(a.updated_at ?? a.created_at ?? '')).slice(0, 3);
  return <aside className="plan-panel production-pulse">
    <div className="plan-panel__head"><span><small>LIVE WORKSPACE DATA</small><strong>Usage and delivery</strong></span></div>
    <div className="usage-facts">
      <UsageFact label="Publications" used={remote.usage.publications.used} limit={remote.usage.publications.limit} />
      <UsageFact label="Assistant messages" used={remote.usage.assistantMessages.used} limit={remote.usage.assistantMessages.limit} />
      <UsageFact label="Connected channels" used={remote.usage.connectedChannels.used} limit={remote.usage.connectedChannels.limit} />
    </div>
    <div className="next-up"><span className="panel-label">RECENT PUBLICATIONS</span>{recent.length === 0 ? <p className="pulse-empty">No publication records yet.</p> : recent.map((publication) => <article key={publication.publication_id}><PlatformBadge provider={publication.provider} /><div><strong>{publicationStatusLabel(publication.state)}</strong><span>{formatDateTime(publication.scheduled_at)}</span></div></article>)}</div>
    <button className="plan-action" onClick={onOpenCalendar}><CalendarClock size={16} /> Open publishing schedule <ArrowRight size={14} /></button>
  </aside>;
}

function PublicationList({ remote, publications, onCancel }: { remote: RemoteWorkspace; publications: RemotePublication[]; onCancel: (publication: RemotePublication) => Promise<void> }) {
  const [busyId, setBusyId] = useState<string>();
  const [error, setError] = useState<string>();
  if (publications.length === 0) return <HonestEmpty icon={<CalendarClock size={22} />} title="No publication records" copy="Nothing has been scheduled or published from this workspace yet." />;
  async function cancel(publication: RemotePublication) {
    setBusyId(publication.publication_id);
    setError(undefined);
    try { await onCancel(publication); } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'The publication could not be canceled.');
    } finally { setBusyId(undefined); }
  }
  return <>{error && <p className="inline-warning"><AlertTriangle size={13} /> {error}</p>}<div className="publication-list">{publications.map((publication) => {
    const integration = remote.integrations.find((item) => item.integration_id === publication.integration_id);
    const link = providerPermalink(publication);
    return <article className="publication-card" key={publication.publication_id}>
      <header><div><PlatformBadge provider={publication.provider} /><span><strong>{providerLabels[publication.provider]}</strong><small>{integration?.provider_account_name ?? maskIdentifier(integration?.provider_account_id)}</small></span></div><StatusDot tone={publicationStatusTone(publication.state)}>{publicationStatusLabel(publication.state)}</StatusDot></header>
      <p>{publication.approval_snapshot?.target?.caption?.trim() || 'No caption is available in this record.'}</p>
      <dl><div><dt>Scheduled</dt><dd>{formatDateTime(publication.scheduled_at)}</dd></div><div><dt>Provider status</dt><dd>{publication.provider_status ? humanize(publication.provider_status) : 'Not reported yet'}</dd></div><div><dt>External ID</dt><dd>{publication.provider_external_id ? <code>{publication.provider_external_id}</code> : 'Not returned yet'}</dd></div>{publication.error_code && <div><dt>Attention code</dt><dd>{publication.error_code}</dd></div>}</dl>
      <footer><small>Publication {publication.publication_id}</small><div>{link && <a className="button button--outline" href={link} target="_blank" rel="noreferrer">Open on YouTube <ExternalLink size={13} /></a>}{cancelableStates.has(publication.state) && <button className="button button--outline" disabled={busyId === publication.publication_id} onClick={() => void cancel(publication)}>{busyId === publication.publication_id ? 'Canceling…' : 'Cancel schedule'}</button>}</div></footer>
    </article>;
  })}</div></>;
}

function Metric({ label, value }: { label: string; value: number }) {
  return <article><span>{label}</span><strong>{value}</strong><small>workspace records</small></article>;
}

function UsageFact({ label, used, limit }: { label: string; used: number; limit: number }) {
  const percentage = limit > 0 ? Math.min(100, (used / limit) * 100) : 0;
  return <div><span><strong>{label}</strong><small>{used} of {limit || '—'}</small></span><i><b style={{ width: `${percentage}%` }} /></i></div>;
}

function LoadingState() {
  return <div className="honest-empty"><span className="loading-mark" /><div><strong>Loading workspace data…</strong><p>No sample records are shown while the authenticated workspace loads.</p></div></div>;
}

function HonestEmpty({ icon, title, copy }: { icon: React.ReactNode; title: string; copy: string }) {
  return <div className="honest-empty">{icon}<div><strong>{title}</strong><p>{copy}</p></div></div>;
}

function formatDateTime(value?: string): string {
  if (!value) return 'Not scheduled';
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return 'Invalid timestamp';
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(date);
}

function humanize(value: string): string {
  return value.replace(/[_-]+/g, ' ').toLowerCase().replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function maskIdentifier(value?: string): string {
  return value ? `Destination ••••${value.slice(-5)}` : 'Destination name unavailable';
}

function initials(value: string): string {
  return value.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]?.toUpperCase()).join('') || 'W';
}
